import { Head, router, usePage } from '@inertiajs/react'
import { useEffect, type ReactNode } from 'react'
import { toast } from 'sonner'
import BewaveFooter from '@/components/public/BewaveFooter'
import PublicHeader from '@/components/public/PublicHeader'
import PublicUserMenu from '@/components/public/PublicUserMenu'
import { AUTH_BACKDROP } from '@/components/public/publicShell'
import { cn } from '@/lib/utils'
import type { CreatorAuthProp } from '@/types/creator'
import type { CityOption } from '@/types/publicSchool'

interface Props {
  title: string
  cities: CityOption[]
  /**
   * Ações do canto direito do header. Sem passar, vale o default — que já muda
   * sozinho conforme haja criador logado (PublicUserMenu). O login/cadastro
   * passa `null` para não ter nenhuma.
   */
  actions?: ReactNode
  /**
   * A ilustração da marca ao fundo do miolo, a mesma do login do admin
   * (AUTH_BACKDROP). Ligada nas telas de entrar/criar conta/confirmar e-mail —
   * é o mesmo momento do produto, e sem ela as duas contas da BeWave pareciam
   * dois sites. O painel fica sem: lá o miolo é trabalho, não boas-vindas.
   */
  backdrop?: boolean
  children: ReactNode
}

/**
 * Casca das telas do criador de página (BeWave Explore): o MESMO header e
 * rodapé públicos da landing/diretório, com o miolo branco. De propósito não
 * usa nada do admin (DashboardLayout/sidebar) — a área do criador é um produto
 * à parte, fora do painel da escola.
 *
 * Com criador LOGADO o header enxuga: sai o filtro de cidade e some o CTA
 * "Quero a BeWave", ficando a navegação e o avatar da conta. A navegação FICA
 * porque é ela que carrega "Minhas páginas" (PublicHeader) — antes esse caminho
 * estava dentro do menu do avatar, e escondê-lo aqui deixava a pessoa sem volta
 * para a lista das próprias páginas.
 *
 * A decisão é desta casca, e não do PublicHeader: no diretório o mesmo criador
 * logado precisa do filtro de cidade, que é o que faz a lista funcionar.
 */
export default function CreatorShell({
  title,
  cities,
  actions,
  backdrop = false,
  children,
}: Props) {
  const { creator_auth } = usePage<{ creator_auth?: CreatorAuthProp }>().props
  const signedIn = Boolean(creator_auth?.creator)

  useFlashToast()

  return (
    <div className="flex min-h-screen flex-col bg-white text-gray-900 antialiased">
      <Head title={title} />

      <PublicHeader
        brandHref="/"
        cities={signedIn ? undefined : cities}
        actions={signedIn ? <PublicUserMenu /> : actions}
      />

      <main
        className={cn(
          'flex flex-1 flex-col',
          backdrop ? AUTH_BACKDROP : 'bg-white',
        )}
      >
        {children}
      </main>

      <BewaveFooter />
    </div>
  )
}

/**
 * Mostra em toast o `notice`/`alert` que o controller mandou.
 *
 * O ApplicationController já compartilha o flash como prop do Inertia e o
 * Layout já monta o `<Toaster />` — mas NENHUMA tela do criador lia isso. O
 * resultado era que "Perfil atualizado", "Página atualizada" e "Página
 * excluída" eram definidos no servidor e descartados em silêncio: a pessoa
 * salvava e a tela não dizia nada.
 *
 * Fica na casca, e não em cada página, porque é comportamento de TODAS elas —
 * e porque a próxima tela do criador nasce com o aviso funcionando sem
 * ninguém lembrar de ligar.
 *
 * Escuta o evento `success` do router em vez de observar a prop num
 * `useEffect`: a mesma ação repetida devolve o MESMO texto de flash, e um
 * efeito com dependência no texto não dispararia na segunda vez — salvar duas
 * vezes seguidas mostraria o aviso só na primeira.
 */
function useFlashToast() {
  useEffect(
    () =>
      router.on('success', (event) => {
        const flash = (
          event.detail.page.props as {
            flash?: { notice?: string | null; alert?: string | null }
          }
        ).flash

        if (flash?.notice) toast.success(flash.notice)
        if (flash?.alert) toast.error(flash.alert)
      }),
    [],
  )
}
